import { useEffect, useRef, useCallback } from "react";
import { driver } from "driver.js";
import "driver.js/dist/driver.css";
import api from "@/lib/api";
import { useAuth } from "@/contexts/AuthContext";
import { useLocation, useNavigate } from "react-router-dom";

/** Steps of the first-run tour. Each entry targets a `data-testid` selector
 *  that already exists in the codebase — keeps the tour brittle-free as long
 *  as those testids stay around (which they do for QA anyway). The `route`
 *  field tells the runner where each step needs to be visible — most live on
 *  /dashboard but a couple deep-link to other pages.
 */
const TOUR_STEPS = [
  {
    route: "/dashboard",
    element: '[data-testid="brand-link"]',
    popover: {
      title: "Welcome to the registry",
      description:
        "This 60-second tour will show you the essentials. You can stop any time — your settings page can replay it.",
      side: "bottom",
      align: "start",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="new-dog-btn"]',
    popover: {
      title: "Register your dogs",
      description:
        "Whether you have one cherished pet or a kennel full of working dogs, this is where their records start. Add registration numbers, photos, parents, and breed info.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="nav-pedigree"]',
    popover: {
      title: "Build the pedigree",
      description:
        "Once a dog has a sire and dam, the pedigree page draws up to six generations and exports a printable PDF certificate.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="nav-breeders"]',
    popover: {
      title: "Find kennels & dog lovers",
      description:
        "The Breeders directory lists every kennel and serious owner on the platform. Tap any profile to see their dogs, available studs, and contact details.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="nav-pawprints"]',
    popover: {
      title: "The Dog's Voice — the dog-perspective feed",
      description:
        "Post photos and updates from your dog's point of view. Like, comment, and share — perfect for breeders and proud pet owners alike.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="search-trigger"], [data-testid="search-trigger-mobile"]',
    popover: {
      title: "Universal search · ⌘K",
      description:
        "Anywhere in the app, hit ⌘K (or /) to jump to a dog, owner, kennel, litter, event, or post.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="nav-inbox-icon"]',
    popover: {
      title: "Your inbox",
      description:
        "Transfer requests, broadcast announcements, and admin replies all land here. The red badge shows unread count.",
    },
  },
  {
    route: "/dashboard",
    element: '[data-testid="nav-settings"]',
    popover: {
      title: "Settings & replay",
      description:
        "Notification preferences live here — and so does the button to replay this tour any time. You're all set!",
    },
  },
];

/** Shared driver.js config so the auto-start and the manual replay use the
 *  same look-and-feel. */
const DRIVER_OPTIONS = {
  showProgress: true,
  showButtons: ["next", "previous", "close"],
  nextBtnText: "Next →",
  prevBtnText: "← Back",
  doneBtnText: "Got it",
  progressText: "Step {{current}} of {{total}}",
  overlayColor: "#0E0E0E",
  overlayOpacity: 0.55,
  // Driver.js animates element-to-element; keep it snappy.
  animate: true,
  smoothScroll: true,
};

/** localStorage key for the per-user "tour seen" flag. Per-user so a second
 *  account on the same browser still gets its own first-run experience. */
const tourFlagKey = (userId) => `pawprints_tour_seen:${userId || "anon"}`;

/** Hook that wires the product tour into the app. It exposes:
 *    - `start()`  → kick off the tour from the current page
 *    - `replay()` → reset server-side completion + start
 *  And it auto-starts the tour the first time an authed user lands on
 *  `/dashboard` if they haven't completed/dismissed it yet.
 */
export default function useProductTour() {
  const { user } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const driverRef = useRef(null);
  const autoStartedRef = useRef(false);

  // Wait for an element matching `selector` to appear in the DOM. Driver.js
  // does its own DOM lookup but we use this so we can navigate the user to
  // the right page and let the route-tree mount before kicking off the step.
  const waitForElement = (selector, timeout = 4000) =>
    new Promise((resolve) => {
      const found = document.querySelector(selector);
      if (found) return resolve(found);
      const start = Date.now();
      const interval = setInterval(() => {
        const el = document.querySelector(selector);
        if (el) {
          clearInterval(interval);
          resolve(el);
        } else if (Date.now() - start > timeout) {
          clearInterval(interval);
          resolve(null);
        }
      }, 100);
    });

  /** Mark the tour as seen — both in localStorage (fast path, survives
   *  failed API calls / closed tabs / logged-out states) and on the server
   *  (cross-device persistence). Called the *instant* the tour drives so
   *  even a refresh-mid-tour or close-tab-immediately doesn't cause the
   *  tour to re-appear on the next login. */
  const markSeen = useCallback(() => {
    if (user?.id) {
      try { localStorage.setItem(tourFlagKey(user.id), new Date().toISOString()); } catch { /* private mode */ }
    }
    api.post("/me/tour/complete").catch(() => { /* non-fatal */ });
  }, [user?.id]);

  const buildSteps = useCallback(() => {
    return TOUR_STEPS.map((step) => ({
      element: step.element,
      popover: { ...step.popover },
      _route: step.route,
    }));
  }, []);

  const buildDriver = useCallback(() => {
    const steps = buildSteps();
    return driver({
      ...DRIVER_OPTIONS,
      steps,
      onHighlightStarted: (element, step) => {
        // If the step needs a different route than we're on, push the user
        // there before the popover is shown.
        const wanted = step._route;
        if (wanted && wanted !== window.location.pathname) {
          navigate(wanted);
        }
      },
      // Belt-and-suspenders: re-mark on destroy too. `markSeen` is idempotent
      // server-side so calling it twice is harmless.
      onDestroyed: () => { markSeen(); },
    });
  }, [buildSteps, markSeen, navigate]);

  /** Manual / replay entrypoint — usable from any page. Will navigate to
   *  /dashboard first since that's where most of the targeted elements live. */
  const start = useCallback(async () => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
    }
    if (window.location.pathname !== "/dashboard") {
      navigate("/dashboard");
    }
    // Wait for the first step's anchor to be in the DOM before driving.
    await waitForElement(TOUR_STEPS[0].element);
    // Mark seen *as soon as the tour starts* — not on destroy. This is the
    // critical fix: if the user reloads, logs out, or closes the tab mid-
    // tour the flag is already persisted so it never re-appears.
    markSeen();
    const d = buildDriver();
    driverRef.current = d;
    d.drive();
  }, [buildDriver, markSeen, navigate]);

  /** Reset server-side flag *and* localStorage flag, then start. */
  const replay = useCallback(async () => {
    if (user?.id) {
      try { localStorage.removeItem(tourFlagKey(user.id)); } catch { /* no-op */ }
    }
    try { await api.post("/me/tour/replay"); } catch { /* non-fatal */ }
    await start();
  }, [start, user?.id]);

  // Auto-start on first visit to /dashboard for an authed user.
  useEffect(() => {
    if (!user) return;
    if (autoStartedRef.current) return;
    if (location.pathname !== "/dashboard") return;

    // Fast path: per-user localStorage flag means we've already shown (or
    // started) the tour for this account on this browser — skip without
    // even hitting the API.
    let localFlag = null;
    try { localFlag = localStorage.getItem(tourFlagKey(user.id)); } catch { /* private mode */ }
    if (localFlag) {
      autoStartedRef.current = true;
      return;
    }

    let cancelled = false;
    const driveNow = async () => {
      // Wait for the dashboard CTAs to mount before we drive.
      await waitForElement(TOUR_STEPS[1].element);
      if (cancelled) return;
      autoStartedRef.current = true;
      markSeen(); // mark *before* drive()
      const d = buildDriver();
      driverRef.current = d;
      d.drive();
    };

    (async () => {
      try {
        const { data } = await api.get("/me/tour");
        if (cancelled) return;
        if (data?.completed_at) {
          // Server says done — propagate to localStorage so subsequent
          // mounts skip the API call entirely.
          try { localStorage.setItem(tourFlagKey(user.id), data.completed_at); } catch { /* no-op */ }
          autoStartedRef.current = true;
          return;
        }
        // Defer to the PersonaPicker on first run: if the user hasn't picked
        // a persona yet, let that micro-onboarding go first so we don't
        // overlay two modals at once. Once the picker resolves (either a
        // selection or an explicit skip) it dispatches a window event that
        // we listen for below to kick off the tour.
        let personaResolved = true;
        try {
          const personaResp = await api.get("/me/persona");
          personaResolved = !!personaResp.data?.persona;
        } catch {
          /* if the persona check fails, fall through and start the tour */
        }
        if (cancelled) return;
        if (personaResolved) {
          await driveNow();
        } else {
          const onResolved = () => {
            window.removeEventListener("persona-changed", onResolved);
            window.removeEventListener("persona-skipped", onResolved);
            if (cancelled || autoStartedRef.current) return;
            // Small delay so the dialog close animation finishes first.
            setTimeout(() => { if (!cancelled) driveNow(); }, 350);
          };
          window.addEventListener("persona-changed", onResolved);
          window.addEventListener("persona-skipped", onResolved);
        }
      } catch {
        /* swallow — tour is non-essential */
      }
    })();
    return () => { cancelled = true; };
  }, [user, location.pathname, buildDriver, markSeen]);

  // Hard cleanup if the component unmounts mid-tour.
  useEffect(() => () => {
    if (driverRef.current) {
      try { driverRef.current.destroy(); } catch { /* no-op */ }
    }
  }, []);

  return { start, replay };
}
